import { useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const vehicleFields = [
  { name: "brand", label: "Marca", md: 4 },
  { name: "model", label: "Modelo", md: 4 },
  {
    name: "bodywork",
    label: "Carrocería",
    md: 4,
    options: ["SEDAN", "SUV", "MINIVAN", "MINIBUS", "BUS"],
  },
  { name: "plate", label: "Placa", md: 4 },
  { name: "year", label: "Año", md: 4, type: "number" },
  { name: "color", label: "Color", md: 4 },
  { name: "cylinder", label: "Cilindrada", md: 3 },
  { name: "weight", label: "Peso", md: 3 },
  { name: "engine", label: "Motor", md: 3 },
  { name: "series", label: "Serie", md: 3 },
];

const ownerFields = [
  { name: "owner", label: "Propietario", md: 4 },
  { name: "owner_dni", label: "DNI del propietario", md: 4 },
  { name: "owner_address", label: "Dirección del propietario", md: 4 },
  {
    name: "premium",
    label: "Premium",
    md: 4,
    options: [
      { value: 0, label: "No" },
      { value: 1, label: "Sí" },
    ],
  },
];

function asDate(value) {
  return value ? dayjs(typeof value === "string" ? value.slice(0, 10) : value) : null;
}

function initialValues(vehicle) {
  return {
    brand: vehicle.brand ?? "",
    model: vehicle.model ?? "",
    bodywork: vehicle.bodywork ?? "SEDAN",
    plate: vehicle.plate ?? "",
    year: vehicle.year ?? "",
    color: vehicle.color ?? "",
    cylinder: vehicle.cylinder ?? "",
    weight: vehicle.weight ?? "",
    engine: vehicle.engine ?? "",
    series: vehicle.series ?? "",
    tr_description: vehicle.tr_description ?? "",
    tr_end: asDate(vehicle.tr_end),
    soat_description: vehicle.soat_description ?? "",
    soat_end: asDate(vehicle.soat_end),
    setare_permission: Boolean(Number(vehicle.setare_permission)),
    setare_start: asDate(vehicle.setare_start),
    setare_end: asDate(vehicle.setare_end),
    owner: vehicle.owner ?? "",
    owner_dni: vehicle.owner_dni ?? "",
    owner_address: vehicle.owner_address ?? "",
    premium: Number(vehicle.premium) ? 1 : 0,
  };
}

function Section({ number, title, description, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        overflow: "hidden",
        borderRadius: 3,
        border: "1px solid rgba(20, 83, 45, 0.14)",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          borderBottom: "1px solid rgba(20, 83, 45, 0.12)",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 34,
              height: 34,
              display: "grid",
              placeItems: "center",
              borderRadius: 2,
              bgcolor: "#165c53",
              color: "#fff",
              fontWeight: 800,
            }}
          >
            {number}
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ color: "#12483f", fontWeight: 800, lineHeight: 1.2 }}
            >
              {title}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <Box sx={{ p: { xs: 2, md: 3 } }}>{children}</Box>
    </Paper>
  );
}

function Fields({ fields, values, onChange, readOnly }) {
  return (
    <Grid container spacing={2}>
      {fields.map(({ name, label, md, type, options }) => (
        <Grid item xs={12} sm={6} md={md} key={name}>
          <TextField
            fullWidth
            select={!!options}
            color="success"
            variant="outlined"
            name={name}
            label={label}
            type={type ?? "text"}
            value={values[name]}
            onChange={(event) => onChange(name, event.target.value)}
            disabled={readOnly}
            inputProps={
              !options && type !== "number"
                ? { style: { textTransform: "uppercase" } }
                : undefined
            }
          >
            {options?.map((option) => {
              const value = typeof option === "string" ? option : option.value;
              return (
                <MenuItem key={String(value)} value={value}>
                  {typeof option === "string" ? option : option.label}
                </MenuItem>
              );
            })}
          </TextField>
        </Grid>
      ))}
    </Grid>
  );
}

function DocumentCard({ title, description, children }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid rgba(20, 83, 45, 0.12)",
        height: "100%",
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, color: "#12483f", mb: 0.5 }}
      >
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        {description}
      </Typography>
      <Stack spacing={2}>{children}</Stack>
    </Box>
  );
}

export function UpdateVehicleForm({ vehicle, readOnly = false }) {
  const [values, setValues] = useState(() => initialValues(vehicle));
  const change = (name, value) =>
    setValues((current) => ({ ...current, [name]: value }));

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack
        spacing={1}
        sx={{ width: "100%", maxWidth: 1100, mx: "auto", py: 2 }}
      >
        <Section
          number="01"
          title="Vehículo"
          description="Datos principales e identificación del vehículo"
        >
          <Fields fields={vehicleFields} values={values} onChange={change} readOnly={readOnly} />
        </Section>

        <Section
          number="02"
          title="Documentos"
          description="Revisión técnica, SOAT y permiso SETARE / TUC"
        >
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={4}>
              <DocumentCard
                title="Revisión técnica"
                description="Información y vencimiento de la revisión"
              >
                <TextField
                  fullWidth
                  color="success"
                  label="Descripción"
                  value={values.tr_description}
                  disabled={readOnly}
                  onChange={(event) =>
                    change("tr_description", event.target.value)
                  }
                />
                <MobileDatePicker
                  label="Fecha de vencimiento"
                  inputFormat="DD/MM/YYYY"
                  value={values.tr_end}
                  disabled={readOnly}
                  onChange={(value) => change("tr_end", value)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </DocumentCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <DocumentCard
                title="SOAT"
                description="Seguro obligatorio y fecha de vencimiento"
              >
                <TextField
                  fullWidth
                  color="success"
                  label="Descripción"
                  value={values.soat_description}
                  disabled={readOnly}
                  onChange={(event) =>
                    change("soat_description", event.target.value)
                  }
                />
                <MobileDatePicker
                  label="Fecha de vencimiento"
                  inputFormat="DD/MM/YYYY"
                  value={values.soat_end}
                  disabled={readOnly}
                  onChange={(value) => change("soat_end", value)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </DocumentCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <DocumentCard
                title="SETARE / TUC"
                description="Permiso y periodo de vigencia"
              >
                <FormControlLabel
                  label="Tiene permiso"
                  control={
                    <Checkbox
                      color="success"
                      checked={values.setare_permission}
                      disabled={readOnly}
                      onChange={(event) =>
                        change("setare_permission", event.target.checked)
                      }
                    />
                  }
                />
                <MobileDatePicker
                  label="Fecha de inicio"
                  inputFormat="DD/MM/YYYY"
                  value={values.setare_start}
                  onChange={(value) => change("setare_start", value)}
                  disabled={readOnly || !values.setare_permission}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
                <MobileDatePicker
                  label="Fecha de vencimiento"
                  inputFormat="DD/MM/YYYY"
                  value={values.setare_end}
                  onChange={(value) => change("setare_end", value)}
                  disabled={readOnly || !values.setare_permission}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </DocumentCard>
            </Grid>
          </Grid>
        </Section>

        <Section
          number="03"
          title="Datos adicionales"
          description="Propietario y categoría premium"
        >
          <Fields fields={ownerFields} values={values} onChange={change} readOnly={readOnly} />
        </Section>
      </Stack>
    </LocalizationProvider>
  );
}
